import { useEffect } from "react";
import LazyMap from "@/components/site/LazyMap";
import { Link } from "@/lib/router-compat";
import {
  Phone,
  ShieldCheck,
  Lock,
  Check,
  Users,
  Gavel,
  Briefcase,
  Shield,
  Baby,
  Award,
  User,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/site/Header";
import SEOHead from "@/components/SEOHead";
import Footer from "@/components/site/Footer";
import FloatingCTAs from "@/components/site/FloatingCTAs";
import PageHero from "@/components/site/PageHero";
import HeroTrustBar from "@/components/site/HeroTrustBar";
import { useInsuranceModal } from "@/components/site/InsuranceModalContext";
import InsurancePills from "@/components/site/InsurancePills";
import RelatedLinks from "@/components/site/RelatedLinks";

const PHONE = "856-200-3127";
const TEL = "tel:8562003127";

const faqs = [
  {
    q: "What is a drug and alcohol evaluation?",
    a: "A drug and alcohol evaluation is a structured clinical assessment conducted by a licensed professional to determine the nature and severity of a person's substance use and provide a recommendation about whether treatment or intervention is appropriate. It typically involves a clinical interview, standardized assessment tools, a review of relevant history, and a written report with findings and recommendations.",
  },
  {
    q: "How long does a drug and alcohol evaluation take?",
    a: "Most drug and alcohol evaluations take between one and two hours including the interview, assessment tools, and initial review. The written report is typically completed within a few business days following the evaluation appointment. Call us at 856-200-3127 to confirm the timeline for your specific evaluation type.",
  },
  {
    q: "Can you do court-ordered evaluations in New Jersey?",
    a: "Our clinical team conducts evaluations that may meet court requirements in New Jersey. Because court orders vary in their specific requirements — the credentials required, the format of the report, and whether the evaluator must be on an approved list — we encourage you to call us first at 856-200-3127 so we can confirm whether our evaluation meets your specific court's requirements before you schedule.",
  },
  {
    q: "What happens if treatment is recommended after my evaluation?",
    a: "If your evaluation results in a recommendation for treatment, our PHP, IOP, Virtual IOP, and dual-diagnosis programs are available on-site at Cherry Hill Recovery Center. There is no obligation to pursue treatment here or anywhere else — the recommendation is a clinical finding, and what you do with it is your decision. If you do choose to pursue treatment, our admissions team handles insurance verification and program intake immediately.",
  },
  {
    q: "Is the evaluation confidential?",
    a: "Yes. All clinical evaluations at Cherry Hill Recovery Center are conducted under standard HIPAA confidentiality protections. Your information is not shared without your written consent — except where you have authorized release to a court, employer, or other requesting party as part of the evaluation process. Our team will explain exactly what is shared with whom before your evaluation begins.",
  },
  {
    q: "What should I bring to my evaluation?",
    a: "Bring a valid photo ID, your insurance card, any documentation from the court, employer, or agency that ordered the evaluation including their specific requirements, and any prior treatment records or relevant medical documentation if available. Our admissions team will confirm exactly what you need when you call to schedule.",
  },
  {
    q: "Does insurance cover a drug and alcohol evaluation?",
    a: "Coverage varies by insurance plan. Many major plans cover clinical evaluations under behavioral health benefits. Our admissions team verifies your specific coverage before your appointment so you know your costs upfront. Call 856-200-3127 or use our insurance verification to get started.",
  },
  {
    q: "What if I disagree with the evaluation findings?",
    a: "If you have concerns about the findings or recommendations in your evaluation report, our clinical team is available to discuss them with you. A second clinical opinion is always an option. We encourage open dialogue — our goal is an accurate clinical assessment, and your perspective is part of that process.",
  },
  {
    q: "How soon can I get an appointment?",
    a: "Most evaluations are scheduled within a few business days, and often sooner when there is a court or employer deadline. Tell our admissions team the date you need the report by when you call 856-200-3127 and we will work backward from it.",
  },
  {
    q: "What is the difference between a drug and alcohol evaluation and a psychiatric evaluation?",
    a: "A drug and alcohol evaluation focuses on substance use — how much, how often, the consequences, and what level of care fits. A psychiatric evaluation focuses on mental health diagnosis and medication. Many people need both, and we provide both on-site, so one appointment can lead directly into the other.",
  },
  {
    q: "Can the evaluation be done by telehealth?",
    a: "In many cases, yes. Clinical evaluations can be conducted by secure video anywhere in New Jersey. Some court, employer, or licensing requirements specify an in-person assessment, so call 856-200-3127 first and we will confirm which format satisfies your requirement.",
  },
  {
    q: "Do I have to enter treatment at Cherry Hill Recovery Center afterward?",
    a: "No. The evaluation and the treatment decision are separate. If treatment is recommended, our PHP, IOP, Virtual IOP, and MAT programs are available here — but you are free to take the report anywhere, and we will help you find another program if ours is not the right fit.",
  },
];

const insurers = [
  "Aetna", "Anthem", "Blue Cross Blue Shield", "Cigna", "United Healthcare",
  "Humana", "Magellan", "Beacon", "Compsych", "Health Net",
  "Independence Administrators", "Carefirst", "& More",
];

const areas = [
  "Cherry Hill", "Camden", "Voorhees", "Marlton", "Moorestown",
  "Haddonfield", "Mount Laurel", "Medford", "Evesham", "Pennsauken",
  "Collingswood", "Burlington County", "Gloucester County",
  "Atlantic County", "Philadelphia Area",
];

const evaluationTypes = [
  {
    Icon: Gavel,
    title: "Court-Ordered Evaluations",
    body: "Courts in New Jersey frequently require drug and alcohol evaluations as part of DUI/DWI proceedings, drug possession charges, domestic cases, and other matters where substance use is a factor. Our clinical team produces professionally documented evaluation reports suitable for court submission. Call us to confirm we can meet the specific requirements of your court order.",
  },
  {
    Icon: Briefcase,
    title: "Employer & EAP Referral Evaluations",
    body: "Many employers and Employee Assistance Programs require a drug and alcohol evaluation before returning to work following a substance-related incident. Our evaluations are conducted by licensed professionals and documented to meet employer and EAP requirements. Call us to discuss your specific employer's needs.",
  },
  {
    Icon: Shield,
    title: "Probation & Parole Evaluations",
    body: "Probation and parole conditions frequently include requirements for drug and alcohol evaluation and compliance documentation. Our clinical team is experienced working with probation-related evaluation requirements across New Jersey. Call us to find out if we can help with your specific probation requirements.",
  },
  {
    Icon: Baby,
    title: "Family Court & CPS Evaluations",
    body: "Child protective services cases and family court proceedings sometimes require drug and alcohol evaluations as part of reunification, custody, or case review processes. Our clinical team understands the documentation requirements for these situations. Call us to discuss whether we can help with your case.",
  },
  {
    Icon: Award,
    title: "Professional Licensing Evaluations",
    body: "Licensing boards for healthcare workers, attorneys, educators, and other licensed professionals sometimes require drug and alcohol evaluations as part of disciplinary or reinstatement processes. Call us to find out if our evaluation format meets your board's specific requirements.",
  },
  {
    Icon: User,
    title: "Personal & Voluntary Evaluations",
    body: "Not every evaluation is ordered by someone else. Some people seek an evaluation because they want an honest clinical assessment of their own substance use — and a professional recommendation about whether any help is warranted. If that is you, we welcome the call. There is no judgment here and no obligation beyond the evaluation itself.",
  },
];

const processSteps = [
  { n: 1, t: "You Call Us First", d: "The process starts with a call to our admissions team at 856-200-3127. We will confirm that we can help with your specific evaluation need, explain the process, answer your questions, and schedule your appointment." },
  { n: 2, t: "Paperwork and Documentation", d: "Before your evaluation you will complete intake paperwork covering your personal history, substance use history, and any relevant documentation required by the court, employer, or referring agency." },
  { n: 3, t: "Clinical Interview", d: "Your evaluation is conducted by a licensed clinician in a private, confidential setting. The interview covers your substance use history, mental health history, family history, current circumstances, and any prior treatment. It is a conversation — not an interrogation." },
  { n: 4, t: "Assessment Tools", d: "Alongside the interview our clinician uses standardized validated assessment instruments to support the clinical findings. These are simple questionnaires — there are no right or wrong answers beyond honest ones." },
  { n: 5, t: "Written Report and Recommendation", d: "Following the evaluation our clinical team produces a written report with clinical findings and a recommendation. This document is provided to you and, with your consent, to the court, employer, or agency that required it." },
];

const afterSteps = [
  { n: 1, t: "Clinical Review", d: "Your evaluation findings are reviewed with you by our clinical team — clearly and without jargon." },
  { n: 2, t: "Level of Care Discussion", d: "We explain what level of care is recommended and why — PHP, IOP, Virtual IOP, or another option." },
  { n: 3, t: "Insurance Verification", d: "Our admissions team verifies your insurance coverage for the recommended program before any commitment." },
  { n: 4, t: "Your Decision", d: "You decide whether to proceed. No pressure, no obligation, no judgment either way." },
];

const DrugAlcoholEvaluationNJ = () => {
  const { open: openInsurance } = useInsuranceModal();

  
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={"Drug & Alcohol Evaluation in Cherry Hill, NJ | Court & Employer"} description={"Need a drug and alcohol evaluation in South Jersey? Licensed clinicians in Cherry Hill complete court, employer and clinical evaluations. Call 856-200-3127."} canonical={"https://cherryhillrecoverycenter.com/drug-alcohol-evaluation-nj"} schemaJson={[...([
      {
        "@context": "https://schema.org",
        "@type": "MedicalClinic",
        name: "Cherry Hill Recovery Center",
        description:
          "Drug and alcohol evaluations in Cherry Hill, NJ for court, employer, probation, and clinical referrals — delivered by a licensed clinical team",
        url: "https://cherryhillrecoverycenter.com/drug-alcohol-evaluation-nj",
        telephone: "856-200-3127",
        address: {
          "@type": "PostalAddress",
          streetAddress: "1930 Marlton Pike East",
          addressLocality: "Cherry Hill",
          addressRegion: "NJ",
          postalCode: "08003",
        },
        knowsAbout: "Addiction Treatment",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://cherryhillrecoverycenter.com/" },
          { "@type": "ListItem", position: 2, name: "Drug & Alcohol Evaluation" },
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ])]} />
      <Header />

      <main className="pt-[120px]">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="bg-[#FAF4F0] border-b border-[#F0E0E0]">
          <div className="container py-3 text-sm flex items-center gap-2 flex-wrap">
            <Link to="/" className="text-[#8B1A1A] hover:underline">Home</Link>
            <ChevronRightSmall />
            <span className="text-[#2C2C2C]">Drug &amp; Alcohol Evaluation</span>
          </div>
        </nav>

        {/* SECTION 1 — HERO */}
        <PageHero
          label="TREATMENT PROGRAMS"
          h1={<>Drug &amp; Alcohol Evaluations in <span style={{ color: "#FAC8C8" }}>Cherry Hill, NJ</span></>}
          subheadline="Court-ordered, employer, probation, or personal — call us and we will let you know if we can help with your specific situation."
        />
        <HeroTrustBar variant="condition" />

        {/* SECTION 2 — WELCOMING CALLOUT */}
        <section
          className="text-center py-5 px-4"
          style={{ background: "#FAF4F0", borderTop: "3px solid #8B1A1A", borderBottom: "3px solid #8B1A1A" }}
        >
          <p className="font-bold text-[16px] max-w-[760px] mx-auto" style={{ color: "#8B1A1A" }}>
            Not sure if we can help with your specific evaluation need? That is exactly what our admissions team is here for — call us and we will give you an honest answer within minutes.
          </p>
          <p className="mt-2">
            <a href={TEL} className="text-xl font-bold hover:underline" style={{ color: "#6B1414" }}>
              Call Cherry Hill Recovery Center — {PHONE}
            </a>
          </p>
          <p className="text-sm mt-1" style={{ color: "#2C2C2C" }}>
            Free. Confidential. No commitment.
          </p>
        </section>

        {/* SECTION 3 — INTRO + SIDEBAR */}
        <section className="bg-white py-20">
          <div className="container grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-8 space-y-6">
              <h2 className="font-display font-bold" style={{ color: "#8B1A1A", fontSize: "30px" }}>
                Drug and Alcohol Evaluations in New Jersey
              </h2>
              <p className="text-[#2C2C2C] leading-relaxed">
                A drug and alcohol evaluation is a structured clinical assessment conducted by a licensed professional to determine the nature and severity of a person's substance use — and to provide a recommendation about what, if any, level of treatment or intervention is appropriate. Evaluations are required in a wide range of situations — from court orders and DUI/DWI requirements to employer referrals, probation conditions, child protective services cases, and personal concerns about one's own use. Cherry Hill Recovery Center's clinical team conducts professional drug and alcohol evaluations in Cherry Hill, NJ for individuals across South Jersey and the greater Philadelphia region. If you are not sure whether we can help with your specific evaluation need — call us at {PHONE} and our admissions team will give you a straightforward answer.
              </p>

              <p className="text-body-dark leading-relaxed">
                <Link to="/what-is-substance-abuse-evaluation-nj" className="text-burgundy underline font-semibold">New to evaluations? Read what to expect</Link>.
              </p>
              <h3 className="font-sans font-semibold pt-4" style={{ color: "#6B1414", fontSize: "22px" }}>
                Who Needs a Drug and Alcohol Evaluation?
              </h3>
              <p className="text-[#2C2C2C] leading-relaxed">
                Drug and alcohol evaluations are required or requested in more situations than most people realize. Some of the most common reasons people contact Cherry Hill Recovery Center for an evaluation include court orders following a DUI, DWI, or drug-related charge, probation or parole requirements, employer or employee assistance program referrals, child protective services or family court requirements, immigration proceedings, professional licensing boards, personal concerns about one's own substance use, and referrals from a primary care provider or mental health professional. Whatever brought you here — call us to find out if we can help with your situation.
              </p>

              <h3 className="font-sans font-semibold pt-4" style={{ color: "#6B1414", fontSize: "22px" }}>
                What a Drug and Alcohol Evaluation Involves
              </h3>
              <p className="text-[#2C2C2C] leading-relaxed">
                A comprehensive drug and alcohol evaluation at Cherry Hill Recovery Center involves a structured clinical interview conducted by a licensed professional — exploring the history, pattern, and impact of substance use, relevant personal and family history, mental health history, current life circumstances, and any prior treatment history. Standardized clinical assessment tools are used alongside the interview. The evaluation concludes with a written report and clinical recommendation — which may include no treatment needed, education, outpatient treatment, intensive outpatient, or a higher level of care depending on the clinical findings. Our clinical team delivers evaluations that are thorough, professionally documented, and accepted by courts, employers, and clinical referral sources across New Jersey.
              </p>
            </div>

            {/* Sticky sidebar */}
            <aside className="lg:col-span-4">
              <div
                className="lg:sticky rounded-xl p-7"
                style={{
                  top: "90px",
                  background: "#FAF4F0",
                  border: "1px solid #F0E0E0",
                  borderTop: "4px solid #8B1A1A",
                }}
              >
                <h3 className="font-bold text-lg mb-2" style={{ color: "#8B1A1A" }}>Not Sure If We Can Help?</h3>
                <p className="text-sm mb-5" style={{ color: "#2C2C2C" }}>
                  Call our admissions team and we will give you a straight answer about whether we can help with your specific evaluation need — no runaround, no commitment required.
                </p>
                <a
                  href={TEL}
                  aria-label={`Call ${PHONE}`}
                  className="block w-full text-center rounded-full font-semibold py-3 mb-3"
                  style={{ background: "#8B1A1A", color: "#FAF4F0" }}
                >
                  Call {PHONE}
                </a>
                <hr className="my-4" style={{ borderColor: "#F0E0E0" }} />
                <button
                  onClick={openInsurance}
                  aria-label="Verify my insurance"
                  className="block w-full text-center rounded-full font-semibold py-3 border-2"
                  style={{ borderColor: "#8B1A1A", color: "#8B1A1A" }}
                >
                  Verify My Insurance
                </button>
                <hr className="my-4" style={{ borderColor: "#F0E0E0" }} />
                <p className="text-[13px] font-bold mb-2" style={{ color: "#2C2C2C" }}>If Treatment Is Recommended:</p>
                <ul className="space-y-1.5 text-sm">
                  {[
                    { l: "Partial Hospitalization (PHP)", h: "/php-cherry-hill-nj" },
                    { l: "Intensive Outpatient (IOP)", h: "/iop-cherry-hill-nj" },
                    { l: "Virtual IOP & Telehealth", h: "/virtual-iop-new-jersey" },
                    { l: "Medication Assisted Treatment", h: "/medication-assisted-treatment-nj" },
                    { l: "Psychiatric Evaluations", h: "/psychiatric-evaluation-nj" },
                  ].map((r) => (
                    <li key={r.l}>
                      <Link to={r.h} className="hover:underline" style={{ color: "#8B1A1A" }}>
                        → {r.l}
                      </Link>
                    </li>
                  ))}
                </ul>
                <hr className="my-4" style={{ borderColor: "#F0E0E0" }} />
                <p className="text-[13px]" style={{ color: "#888888" }}>
                  1930 Marlton Pike East,<br />Cherry Hill, NJ 08003
                </p>
                <a
                  href="https://www.google.com/maps?q=1930+Marlton+Pike+East,+Cherry+Hill,+NJ+08003"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[13px] font-semibold hover:underline mt-1 inline-block"
                  style={{ color: "#8B1A1A" }}
                >
                  Get Directions →
                </a>
              </div>
            </aside>
          </div>
        </section>

        {/* SECTION 4 — TYPES OF EVALUATIONS */}
        <section className="py-20" style={{ background: "#FAF4F0" }}>
          <div className="container max-w-[1100px]">
            <div className="text-center mb-12">
              <h2 className="font-display font-bold mb-4" style={{ color: "#8B1A1A", fontSize: "30px" }}>
                Types of Drug and Alcohol Evaluations We May Be Able to Help With
              </h2>
              <p className="text-[16px] max-w-[760px] mx-auto" style={{ color: "#2C2C2C" }}>
                Every evaluation situation is different. Call us to find out if our clinical team can conduct the specific type of evaluation you need — we will be upfront about whether we are the right fit.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {evaluationTypes.map(({ Icon, title, body }) => (
                <div
                  key={title}
                  className="rounded-xl p-7 bg-white"
                  style={{
                    borderTop: "4px solid #8B1A1A",
                    boxShadow: "0 4px 16px rgba(107,20,20,0.08)",
                  }}
                >
                  <Icon className="h-8 w-8 mb-3" style={{ color: "#8B1A1A" }} aria-hidden="true" />
                  <h3 className="font-bold text-lg mb-3" style={{ color: "#2C2C2C" }}>{title}</h3>
                  <p className="text-[#2C2C2C] leading-relaxed text-[15px]">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 5 — WHAT HAPPENS AFTER */}
        <section className="py-20 bg-white">
          <div className="container grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-7 space-y-5">
              <h2 className="font-display font-bold" style={{ color: "#8B1A1A", fontSize: "32px" }}>
                What Happens After the Evaluation
              </h2>
              <p className="text-[#2C2C2C] leading-relaxed">
                A drug and alcohol evaluation does not automatically mean treatment. The evaluation is a clinical assessment — the recommendation that follows depends entirely on what the assessment finds. Many evaluations result in recommendations for education only, or a finding that no treatment is currently warranted. Others identify a need for outpatient support, intensive outpatient treatment, or a higher level of care.
              </p>
              <p className="text-[#2C2C2C] leading-relaxed">
                One of the advantages of completing your evaluation at Cherry Hill Recovery Center is that if treatment is recommended, you are already in the right place. Our <Link to="/php-cherry-hill-nj" className="underline" style={{ color: "#8B1A1A" }}>PHP</Link>, <Link to="/iop-cherry-hill-nj" className="underline" style={{ color: "#8B1A1A" }}>IOP</Link>, Virtual IOP, and dual-diagnosis programs are available on-site — meaning the transition from evaluation to treatment, if needed, is immediate and seamless. You will not be referred elsewhere and left to figure out the next step.
              </p>
              <p className="text-[#2C2C2C] leading-relaxed">
                If you complete an evaluation with us and treatment is not recommended — or if you choose not to pursue treatment at this time — there is no pressure. Our job is to give you an honest clinical assessment, not to sell you a program. What you do with the results is entirely your decision.
              </p>
            </div>

            <div className="lg:col-span-5">
              <div
                className="rounded-xl p-7"
                style={{
                  background: "#FAF4F0",
                  borderTop: "4px solid #8B1A1A",
                }}
              >
                <h3 className="font-bold text-lg mb-5" style={{ color: "#8B1A1A" }}>
                  If Treatment Is Recommended After Your Evaluation
                </h3>
                <ol className="space-y-4">
                  {afterSteps.map((p) => (
                    <li key={p.n} className="flex gap-3">
                      <span
                        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                        style={{ background: "#8B1A1A", color: "#FAF4F0" }}
                      >
                        {p.n}
                      </span>
                      <div>
                        <p className="font-semibold text-[15px]" style={{ color: "#2C2C2C" }}>{p.t}</p>
                        <p className="text-sm mt-1" style={{ color: "#555555" }}>{p.d}</p>
                      </div>
                    </li>
                  ))}
                </ol>
                <div className="mt-6 pt-5 border-t" style={{ borderColor: "#F0E0E0" }}>
                  <p className="text-[#2C2C2C] mb-1">Questions about what comes next?</p>
                  <a href={TEL} className="font-bold text-lg hover:underline" style={{ color: "#8B1A1A" }}>
                    Call {PHONE} →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 6 — EVALUATION PROCESS */}
        <section className="py-20" style={{ background: "#FAF4F0" }}>
          <div className="container max-w-[1100px]">
            <h2 className="font-display font-bold mb-4" style={{ color: "#8B1A1A", fontSize: "32px" }}>
              What to Expect During Your Evaluation
            </h2>
            <p className="text-[#2C2C2C] leading-relaxed mb-10">
              If you have never had a drug and alcohol evaluation before, not knowing what to expect can make it feel more daunting than it needs to be. Here is what the process typically looks like.
            </p>

            <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-5 mb-10">
              {processSteps.map((p) => (
                <div
                  key={p.n}
                  className="rounded-[10px] p-5 bg-white"
                  style={{ borderTop: "3px solid #8B1A1A" }}
                >
                  <span
                    className="inline-flex w-9 h-9 rounded-full items-center justify-center text-sm font-bold mb-3"
                    style={{ background: "#8B1A1A", color: "#FAF4F0" }}
                  >
                    {p.n}
                  </span>
                  <h3 className="font-bold text-[16px] mb-2" style={{ color: "#2C2C2C" }}>{p.t}</h3>
                  <p className="text-sm leading-relaxed" style={{ color: "#2C2C2C" }}>{p.d}</p>
                </div>
              ))}
            </div>

            <div
              className="rounded-lg p-6"
              style={{ background: "#FFFFFF", borderLeft: "4px solid #8B1A1A" }}
            >
              <h3 className="font-bold text-base mb-2" style={{ color: "#8B1A1A" }}>
                Be Honest — It Helps You More Than It Hurts
              </h3>
              <p className="text-[15px]" style={{ color: "#2C2C2C" }}>
                The most common concern people bring to a drug and alcohol evaluation is that being honest will be used against them. In most cases the opposite is true — clinical evaluators are trained to recognize when someone is minimizing, and minimization itself is a clinical finding. Honest engagement with the process produces the most accurate and most defensible clinical report. Our team is not here to judge you — we are here to give you an honest professional assessment.
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 7 — WHY CHRC */}
        <section className="py-20 bg-white">
          <div className="container max-w-[1100px]">
            <div className="text-center mb-12">
              <h2 className="font-display font-bold mb-4" style={{ color: "#8B1A1A", fontSize: "30px" }}>
                Why Call Cherry Hill Recovery Center First
              </h2>
              <p className="text-[16px] max-w-[760px] mx-auto" style={{ color: "#2C2C2C" }}>
                We know you may have options for where to get your evaluation. Here is why calling us first makes sense.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                { t: "Licensed Clinical Team", d: "Your evaluation is conducted by licensed clinical professionals — not administrative staff. Our team has the credentials and experience to produce evaluation reports that hold up to scrutiny from courts, employers, and licensing boards." },
                { t: "Honest Answers Before You Commit", d: "Call us before scheduling anything. Our admissions team will tell you honestly whether we can help with your specific situation — before you drive here, before you pay anything, before you commit to anything. No runaround." },
                { t: "Treatment On-Site If You Need It", d: "If your evaluation recommends treatment, our PHP, IOP, Virtual IOP, and dual-diagnosis programs are available right here. You will not be handed a list of phone numbers and left to start over somewhere else." },
                { t: "Most Insurance Accepted", d: "Drug and alcohol evaluations and any subsequent treatment are covered by most major insurance plans. Our admissions team verifies your coverage before anything starts — so you know exactly what your costs are upfront." },
              ].map((c) => (
                <div
                  key={c.t}
                  className="rounded-xl p-7"
                  style={{
                    background: "#FAF4F0",
                    borderTop: "4px solid #8B1A1A",
                    boxShadow: "0 4px 16px rgba(107,20,20,0.08)",
                  }}
                >
                  <h3 className="font-bold text-lg mb-3" style={{ color: "#2C2C2C" }}>{c.t}</h3>
                  <p className="text-[#2C2C2C] leading-relaxed text-[15px]">{c.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 7B — WHICH EVALUATION DO YOU NEED */}
        <section className="py-20" style={{ background: "#FAF4F0" }}>
          <div className="container max-w-[1100px]">
            <h2 className="font-display font-bold mb-4" style={{ color: "#8B1A1A", fontSize: "30px" }}>
              Which Evaluation Do You Need?
            </h2>
            <p className="text-[#2C2C2C] leading-relaxed mb-8">
              People call us asking for very different things using the same words. This table shows what each type of assessment covers, so you can tell us what you need in one sentence when you call.
            </p>

            <div className="overflow-x-auto rounded-xl bg-white" style={{ border: "1px solid #F0E0E0" }}>
              <table className="w-full text-left text-[15px]" style={{ borderCollapse: "collapse", minWidth: 640 }}>
                <thead>
                  <tr style={{ background: "#8B1A1A", color: "#FAF4F0" }}>
                    <th className="p-4 font-bold">Type of assessment</th>
                    <th className="p-4 font-bold">Best for</th>
                    <th className="p-4 font-bold">What you get</th>
                  </tr>
                </thead>
                <tbody style={{ color: "#2C2C2C" }}>
                  {[
                    ["Drug and alcohol evaluation", "Court, DUI, employer, licensing board, or family request", "Clinical interview, standardized tools, written report with findings and a level-of-care recommendation"],
                    ["Psychiatric evaluation", "Depression, anxiety, PTSD, bipolar symptoms, or medication questions", "Diagnostic assessment by a psychiatric provider and a medication plan when appropriate"],
                    ["Admissions assessment", "You already know you want treatment here", "Same-day placement into PHP, IOP, or Virtual IOP with insurance verified first"],
                    ["Level of care quiz", "You are still deciding and want a starting point", "A free two-minute self-guided estimate — not a clinical evaluation"],
                  ].map((r) => (
                    <tr key={r[0]} style={{ borderTop: "1px solid #F0E0E0" }}>
                      <td className="p-4 font-semibold">{r[0]}</td>
                      <td className="p-4">{r[1]}</td>
                      <td className="p-4">{r[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="text-[#2C2C2C] leading-relaxed mt-6">
              Not sure which one applies? Compare a{" "}
              <Link to="/psychiatric-evaluation-nj" className="font-semibold hover:underline" style={{ color: "#8B1A1A" }}>psychiatric evaluation in New Jersey</Link>{" "}
              with this page, or take the{" "}
              <Link to="/find-your-level-of-care" className="font-semibold hover:underline" style={{ color: "#8B1A1A" }}>level of care quiz</Link>{" "}
              first. Many people need both a substance use evaluation and a psychiatric evaluation — we can do both here.
            </p>
          </div>
        </section>

        {/* SECTION 7C — AFTER YOUR EVALUATION */}
        <section className="py-20 bg-white">
          <div className="container max-w-[1100px]">
            <h2 className="font-display font-bold mb-4" style={{ color: "#8B1A1A", fontSize: "30px" }}>
              What Happens After Your Evaluation
            </h2>
            <p className="text-[#2C2C2C] leading-relaxed mb-8">
              An evaluation is only useful if it leads somewhere. If treatment is recommended, every level of care below is available at our Cherry Hill office or by telehealth — no waitlist, no starting over with a new intake.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
              {[
                { t: "Medical detox first", d: <>If you are physically dependent on alcohol, benzodiazepines, or opioids, we arrange <Link to="/detox-referral-coordination-nj" className="font-semibold hover:underline" style={{ color: "#8B1A1A" }}>detox referral coordination</Link> and hold your spot for the step down.</> },
                { t: "Partial care (PHP)", d: <><Link to="/php-cherry-hill-nj" className="font-semibold hover:underline" style={{ color: "#8B1A1A" }}>PHP in Cherry Hill</Link> runs five days a week for people who need daily structure but not an overnight stay.</> },
                { t: "Intensive outpatient (IOP)", d: <><Link to="/iop-cherry-hill-nj" className="font-semibold hover:underline" style={{ color: "#8B1A1A" }}>IOP</Link> meets three days a week, with <Link to="/evening-iop-new-jersey" className="font-semibold hover:underline" style={{ color: "#8B1A1A" }}>evening IOP</Link> for people who work days.</> },
                { t: "Statewide telehealth", d: <><Link to="/virtual-iop-new-jersey" className="font-semibold hover:underline" style={{ color: "#8B1A1A" }}>Virtual IOP</Link> serves anyone in New Jersey when travel, childcare, or work makes in-person care hard.</> },
                { t: "Medication support", d: <><Link to="/medication-assisted-treatment-nj" className="font-semibold hover:underline" style={{ color: "#8B1A1A" }}>MAT</Link>, including <Link to="/suboxone-doctor-cherry-hill-nj" className="font-semibold hover:underline" style={{ color: "#8B1A1A" }}>Suboxone</Link> and <Link to="/sublocade-doctor-nj" className="font-semibold hover:underline" style={{ color: "#8B1A1A" }}>Sublocade</Link>, when opioids or alcohol are involved.</> },
                { t: "Stable housing", d: <>If home is not a safe place to start recovery, <Link to="/recovery-housing-nj" className="font-semibold hover:underline" style={{ color: "#8B1A1A" }}>recovery housing</Link> can be coordinated alongside treatment.</> },
              ].map((c) => (
                <div key={c.t} className="rounded-[10px] p-5" style={{ background: "#FAF4F0", borderTop: "3px solid #8B1A1A" }}>
                  <h3 className="font-bold text-[16px] mb-2" style={{ color: "#2C2C2C" }}>{c.t}</h3>
                  <p className="text-[15px] leading-relaxed" style={{ color: "#2C2C2C" }}>{c.d}</p>
                </div>
              ))}
            </div>
            <p className="text-[#2C2C2C] leading-relaxed mt-8">
              No treatment is required after an evaluation. If a different program is a better fit than ours, we will say so and help you find it. Call{" "}
              <a href={TEL} className="font-semibold hover:underline" style={{ color: "#8B1A1A" }}>{PHONE}</a> to talk it through.
            </p>
          </div>
        </section>


        {/* SECTION 8 — INSURANCE */}
        <section className="py-20" style={{ background: "#FAF4F0" }}>
          <div className="container max-w-[1000px] text-center">
            <h2 className="font-display font-bold mb-5" style={{ color: "#8B1A1A", fontSize: "32px" }}>
              Does Insurance Cover Drug and Alcohol Evaluations in New Jersey?
            </h2>
            <p className="text-[#2C2C2C] leading-relaxed mb-5 text-left">
              Coverage for drug and alcohol evaluations varies by insurance plan. Many major insurance plans cover clinical evaluations under behavioral health benefits — but the specifics depend on your plan, provider, and the type of evaluation required. Our admissions team will verify your specific coverage before your appointment so you know exactly what is covered and what, if anything, you will be responsible for. There are no surprises.
            </p>
            <p className="text-[#2C2C2C] leading-relaxed mb-8 text-left">
              If your evaluation results in a recommendation for PHP, IOP, or other addiction treatment, most major insurance plans are required to cover those services under the Mental Health Parity and Addiction Equity Act. Our admissions team handles the entire verification process for you.
            </p>
            <div className="flex flex-wrap justify-center gap-2.5 mb-10">
              <InsurancePills insurers={insurers} style={{ justifyContent: "center" }} />
            </div>
            <button
              onClick={openInsurance}
              aria-label="Verify my insurance — takes 2 minutes"
              className="inline-flex items-center gap-2 rounded-full font-semibold px-10 py-4 hover:opacity-90 transition-opacity"
              style={{ background: "#8B1A1A", color: "#FAF4F0" }}
            >
              Verify My Insurance — Takes 2 Minutes
            </button>
          </div>
        </section>

        {/* SECTION 9 — SERVICE AREA */}
        <section className="py-20 bg-white">
          <div className="container max-w-[1100px]">
            <h2 className="font-display font-bold mb-5" style={{ color: "#8B1A1A", fontSize: "30px" }}>
              Drug and Alcohol Evaluations Serving Cherry Hill and South Jersey
            </h2>
            <p className="text-[#2C2C2C] leading-relaxed mb-8">
              Cherry Hill Recovery Center conducts drug and alcohol evaluations for individuals throughout Camden County, Burlington County, Gloucester County, Atlantic County, and the greater South Jersey and Philadelphia region. We are located at 1930 Marlton Pike East in Cherry Hill, NJ — easily accessible from Routes 70 and 38 and conveniently situated for individuals coming from across South Jersey.
            </p>
            <div className="flex flex-wrap gap-2.5 mb-10">
              {areas.map((a) => (
                <span
                  key={a}
                  className="inline-block rounded-full px-4 py-2 text-sm font-semibold"
                  style={{ background: "#FAF4F0", border: "1px solid #F0E0E0", color: "#8B1A1A" }}
                >
                  {a}
                </span>
              ))}
            </div>
            <div className="rounded-xl overflow-hidden" style={{ border: "1px solid #F0E0E0" }}>
              <LazyMap src="https://www.google.com/maps?q=1930+Marlton+Pike+East,+Cherry+Hill,+NJ+08003&output=embed" title="Cherry Hill Recovery Center location map" height={380} />
            </div>
          </div>
        </section>

        {/* SECTION 10 — MEET THE TEAM */}
        <section className="py-20" style={{ background: "#FAF4F0" }}>
          <div className="container max-w-[1000px]">
            <h2 className="font-display font-bold mb-5" style={{ color: "#8B1A1A", fontSize: "30px" }}>
              Our Clinical Team
            </h2>
            <p className="text-[#2C2C2C] leading-relaxed mb-10">
              Drug and alcohol evaluations at Cherry Hill Recovery Center are conducted by licensed clinical professionals and overseen by our Chief Medical Officer, board-certified psychiatrist Dr. Jeffrey Simon. Every evaluation is conducted with clinical rigor, personal respect, and complete confidentiality.
            </p>
            <div className="grid md:grid-cols-12 gap-8 items-start">
              <div className="md:col-span-4 text-center">
                <div
                  className="w-40 h-40 rounded-full mx-auto flex items-center justify-center"
                  style={{ background: "#FFFFFF", border: "3px solid #8B1A1A" }}
                  role="img"
                  aria-label="Dr. Jeffrey Simon, MD, Chief Medical Officer at Cherry Hill Recovery Center, Cherry Hill NJ"
                >
                  <Users className="h-16 w-16" style={{ color: "#8B1A1A" }} aria-hidden="true" />
                </div>
                <p className="italic mt-3 text-sm" style={{ color: "#888888" }}>
                  Dr. Jeffrey Simon, MD — Chief Medical Officer
                </p>
              </div>
              <div className="md:col-span-8">
                <p className="text-[#2C2C2C] leading-relaxed mb-5">
                  Dr. Simon is a board-certified psychiatrist specializing in addiction medicine and co-occurring mental health disorders. He oversees clinical programming and the medical team at Cherry Hill Recovery Center. His clinical team brings the same depth of expertise and professional standards to drug and alcohol evaluations as to every other aspect of addiction care — producing reports that are thorough, accurate, and professionally defensible.
                </p>
                <Link to="/about" className="font-semibold hover:underline" style={{ color: "#8B1A1A" }}>
                  Meet Our Full Team →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 11 — FAQ */}
        <section className="py-20 bg-white">
          <div className="container max-w-[900px]">
            <h2 className="font-display font-bold mb-8 text-center" style={{ color: "#8B1A1A", fontSize: "32px" }}>
              Frequently Asked Questions About Drug and Alcohol Evaluations in New Jersey
            </h2>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} style={{ borderBottom: "1px solid #F0E0E0" }}>
                  <AccordionTrigger className="text-left font-semibold py-4" style={{ color: "#2C2C2C", fontSize: "16px" }}>
                    <h3 className="m-0 font-semibold text-base">{f.q}</h3>
                  </AccordionTrigger>
                  <AccordionContent className="leading-relaxed" style={{ background: "#FAF4F0", color: "#2C2C2C", fontSize: "15px", padding: "16px 20px" }}>
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* SECTION 12 — BOTTOM CTA */}
        <section
          className="py-20 text-center"
          style={{ background: "linear-gradient(135deg, #6B1414 0%, #8B1A1A 100%)" }}
        >
          <div className="container max-w-[900px]">
            <h2 className="font-display font-bold mb-4" style={{ color: "#FFFFFF", fontSize: "34px" }}>
              Not Sure If We Can Help? That Is What the Call Is For.
            </h2>
            <p className="text-lg mb-8 max-w-[700px] mx-auto" style={{ color: "#FAF4F0" }}>
              Tell our admissions team what you need and we will give you an honest answer right away — no runaround, no commitment, no judgment. If we can help we will tell you exactly how. If we cannot we will point you in the right direction.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
              <a
                href={TEL}
                aria-label={`Call ${PHONE}`}
                className="inline-flex items-center gap-2 rounded-full font-bold px-8 py-4 hover:opacity-90 transition-opacity"
                style={{ background: "#FAF4F0", color: "#8B1A1A" }}
              >
                <Phone className="h-5 w-5" />
                Call {PHONE}
              </a>
              <Link
                to="/contact"
                aria-label="Send us a message"
                className="inline-flex items-center gap-2 rounded-full font-bold px-8 py-4 border-2 hover:bg-white/10 transition-colors"
                style={{ borderColor: "#FAF4F0", color: "#FAF4F0" }}
              >
                Send Us a Message
              </Link>
            </div>
            <div className="flex flex-wrap justify-center gap-x-8 gap-y-2 text-sm" style={{ color: "#FAC8C8" }}>
              <span className="inline-flex items-center gap-2"><Lock className="h-4 w-4" /> 100% Confidential</span>
              <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4" /> Most Insurance Accepted</span>
              <span className="inline-flex items-center gap-2"><Phone className="h-4 w-4" /> Honest Answers — No Runaround</span>
            </div>
          </div>
        </section>
      </main>

      <RelatedLinks exclude={["/drug-alcohol-evaluation-nj"]} />

      <Footer />
      <FloatingCTAs />
    </div>
  );
};

const ChevronRightSmall = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" aria-hidden="true">
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

export default DrugAlcoholEvaluationNJ;
